// v3.0.0-rc.8n — ProductDetailModal : fiche produit avec navigation swipe.
// ─────────────────────────────────────────────────────────────────────────────
// - Accepte `products` (liste complète du carrousel) + `index` (position actuelle)
//   + `onIndexChange` (callback pour prev/next). Compatibilité rétro : si seul
//   `product` est fourni, la liste = [product].
// - Fenêtre CONTEXTUELLE (pas fullscreen) : constrainte à la zone chat via
//   .aya-chat-modal-root avec top/bottom safe-area. Le fond est flouté et laisse
//   voir la page derrière (nav + halo restent visibles/interactifs si tape hors zone).
// - Swipe horizontal : touch (X) + boutons ‹ › + touches ← → clavier + wheel horizontal.
// - Bouton "Boutique" ouvre la fiche marchand ; "Commander maintenant" déclenche
//   l'action de commande. Sur close, le carrousel parent scroll pour ré-aligner
//   la carte visitée (position memory).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { addItem } from "@/lib/localCartStore";
import { trackSignal as _trackPwaSignal } from "@/pwa/valueSignals";

function fmtFcfa(n) {
  if (!Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString("fr-FR") + " FCFA";
}

export default function ProductDetailModal({
  product,           // legacy — single product
  products,          // v3.0.0-rc.8n — swipe list
  index = 0,
  onIndexChange,
  onClose,
  onOrder,
  onViewShop,
}) {
  // Normalise : products[] a priorité, sinon fallback sur product unique.
  const items = useMemo(() => {
    if (Array.isArray(products) && products.length > 0) return products;
    if (product) return [product];
    return [];
  }, [products, product]);
  const safeIndex = Math.max(0, Math.min(items.length - 1, index));
  const current = items[safeIndex] || null;

  const [photoIdx, setPhotoIdx] = useState(0);
  const [added, setAdded] = useState(false);
  // Reset photo index when swiping to another product.
  useEffect(() => { setPhotoIdx(0); setAdded(false); }, [safeIndex]);

  // v3.0.0-rc.8z-pwa — Signal "produit vu" à l'ouverture / au swipe (moment de valeur PWA).
  useEffect(() => { if (current) { try { _trackPwaSignal('product_viewed'); } catch (_) {} } }, [current?.product_id]);

  const goPrev = useCallback(() => {
    if (items.length <= 1) return;
    const next = (safeIndex - 1 + items.length) % items.length;
    onIndexChange?.(next);
  }, [items.length, safeIndex, onIndexChange]);
  const goNext = useCallback(() => {
    if (items.length <= 1) return;
    const next = (safeIndex + 1) % items.length;
    onIndexChange?.(next);
  }, [items.length, safeIndex, onIndexChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goPrev, goNext]);

  // Swipe touch.
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches[0]; touchStartX.current = t.clientX; touchStartY.current = t.clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - (touchStartY.current || 0);
    touchStartX.current = null; touchStartY.current = null;
    if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return; // pas un swipe horizontal net
    if (dx > 0) goPrev(); else goNext();
  };

  const handleAdd = useCallback(() => {
    if (!current) return;
    addItem(current);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  }, [current]);

  if (!current) return null;
  const photos = Array.isArray(current.photos) ? current.photos.filter(Boolean) : [];
  const isPartner = current.provider === "partner";
  const etaLabel = Number.isFinite(current.delivery_eta_min)
    ? (current.delivery_eta_min >= 720 ? `${Math.max(1, Math.round(current.delivery_eta_min / 1440))}-2 j` : `~${current.delivery_eta_min} min`)
    : null;

  const modalRoot = typeof document !== "undefined" ? document.body : null;
  if (!modalRoot) return null;

  const modal = (
    <div
      data-testid="product-detail-root"
      className="aya-chat-modal-root aya-modal-fullscreen"
      style={{ zIndex: 220 }}
    >
      <div
        data-testid="product-detail-backdrop"
        onClick={onClose}
        className="aya-chat-modal-backdrop"
      />
      <article
        data-testid="product-detail-card"
        role="dialog"
        aria-label={current.name}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{
          position: "relative",
          width: "min(720px, 100%)",
          height: "100%",
          maxHeight: "100%",
          overflow: "hidden",
          background: "linear-gradient(180deg, #0B4531 0%, #062A1F 100%)",
          border: "none",
          borderRadius: 0,
          boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
          color: "#F4F1E8",
          display: "flex",
          flexDirection: "column",
          animation: "pdIn 260ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Header — close */}
        <button
          data-testid="product-detail-close"
          onClick={onClose}
          aria-label="Fermer"
          style={{
            position: "absolute", top: 10, right: 10, zIndex: 3,
            width: 32, height: 32, borderRadius: "50%",
            background: "rgba(6,42,31,0.85)", color: "#F4F1E8",
            border: "1px solid rgba(232,213,163,0.35)", cursor: "pointer",
            fontSize: 18, lineHeight: 1,
          }}
        >×</button>

        {/* Nav arrows (visibles si plusieurs items). Sur mobile, aussi swipe. */}
        {items.length > 1 && (
          <>
            <button
              data-testid="product-detail-prev"
              onClick={goPrev}
              aria-label="Produit précédent"
              style={_navArrowStyle("left")}
            >‹</button>
            <button
              data-testid="product-detail-next"
              onClick={goNext}
              aria-label="Produit suivant"
              style={_navArrowStyle("right")}
            >›</button>
            <div
              data-testid="product-detail-counter"
              style={{
                position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)",
                zIndex: 3, padding: "3px 10px", borderRadius: 999,
                background: "rgba(6,42,31,0.85)", color: "#EBD9A8",
                border: "1px solid rgba(232,213,163,0.35)",
                fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
              }}
            >{safeIndex + 1}/{items.length}</div>
          </>
        )}

        {/* Photo */}
        <div style={{ position: "relative", width: "100%", aspectRatio: "16/10", background: "#04180F", flexShrink: 0 }}>
          {photos.length > 0 ? (
            <img
              src={photos[photoIdx % photos.length]}
              alt={current.name}
              onClick={() => photos.length > 1 && setPhotoIdx((v) => (v + 1) % photos.length)}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", cursor: photos.length > 1 ? "pointer" : "default" }}
            />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 72 }}>
              {current.emoji || "🛍"}
            </div>
          )}
          {current.sponsored && (
            <span style={{
              position: "absolute", top: 12, left: 12,
              fontSize: 9, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase",
              padding: "4px 10px", borderRadius: 999,
              background: "rgba(6,42,31,0.85)", color: "#EBD9A8",
              border: "1px solid rgba(232,213,163,0.5)",
            }}>Sponsorisé</span>
          )}
          {photos.length > 1 && (
            <div style={{
              position: "absolute", bottom: 12, right: 12,
              display: "flex", gap: 6,
            }}>
              {photos.map((_, i) => (
                <button
                  key={i}
                  aria-label={`Photo ${i + 1}`}
                  onClick={() => setPhotoIdx(i)}
                  style={{
                    width: 8, height: 8, borderRadius: "50%",
                    border: "none",
                    background: i === photoIdx ? "#EBD9A8" : "rgba(244,241,232,0.4)",
                    cursor: "pointer",
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{
          padding: "16px 18px 18px",
          overflowY: "auto",
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, letterSpacing: -0.3, lineHeight: 1.2 }}>
              {current.name}
            </h2>
            {current.brand && (
              <div style={{ fontSize: 12, opacity: 0.6, marginTop: 3 }}>par {current.brand}</div>
            )}
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#EBD9A8", fontFeatureSettings: "'tnum'" }}>
              {fmtFcfa(current.price_fcfa)}{current.price_suffix || ""}
            </div>
            {Number.isFinite(current.rating) && (
              <div style={{ fontSize: 12, marginBottom: 4 }}>
                <span style={{ color: "#EBD9A8" }}>★ {current.rating}</span>
                {Number.isFinite(current.reviews_count) && (
                  <span style={{ color: "#34D399", marginLeft: 4 }}>({current.reviews_count} avis)</span>
                )}
              </div>
            )}
          </div>

          <div style={{ fontSize: 12.5, opacity: 0.8, lineHeight: 1.55 }}>
            {isPartner ? "Vendu par " : "Vendeur : "}
            <strong style={{ color: "#EBD9A8" }}>
              {current.sold_by || current.merchant_name || "—"}
            </strong>
            {current.merchant_location && <span> · {current.merchant_location}</span>}
            {typeof current.merchant_online === "boolean" && (
              current.merchant_online
                ? <span style={{ color: "#34D399", fontWeight: 700 }}> · 🟢 En ligne</span>
                : <span style={{ opacity: 0.5, fontWeight: 700 }}> · ⚪ Hors ligne</span>
            )}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: 11.5 }}>
            <Chip>{current.available ? "🟢 En stock" : "🔴 Rupture"}</Chip>
            {etaLabel && <Chip>🛵 {etaLabel}</Chip>}
            {Array.isArray(current.sizes) && current.sizes.length > 0 && (
              <Chip>Tailles : {current.sizes.join(", ")}</Chip>
            )}
            {current.category && <Chip>#{current.category}</Chip>}
          </div>

          {/* v3.0.0-rc.8z — Description complète du produit (avec toggle "Voir plus" si long). */}
          {(current.description || current.long_description) && (
            <ProductDescription text={current.long_description || current.description} />
          )}

          {/* v3.0.0-rc.8z — Caractéristiques (specs / attributs clés) si fournies. */}
          {Array.isArray(current.specs) && current.specs.length > 0 && (
            <div style={{ marginTop: 4, borderTop: "1px solid rgba(232,213,163,0.14)", paddingTop: 10 }}>
              <div style={{ fontSize: 11, opacity: 0.6, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Caractéristiques</div>
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 12, rowGap: 4, fontSize: 12.5 }}>
                {current.specs.map((s, i) => (
                  <span key={i} style={{ display: "contents" }}>
                    <span style={{ opacity: 0.65 }}>{s.label || s.k}</span>
                    <span style={{ color: "#EBD9A8", fontWeight: 600 }}>{s.value || s.v}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(current.tags) && current.tags.length > 0 && (
            <div style={{ fontSize: 11, opacity: 0.6, lineHeight: 1.6 }}>
              {current.tags.slice(0, 8).map((t) => <span key={t} style={{ marginRight: 8 }}>#{t}</span>)}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
            <button
              data-testid="product-detail-add-cart"
              onClick={handleAdd}
              disabled={!current.available}
              style={{
                flex: "1 1 140px",
                padding: "12px 14px", borderRadius: 12,
                background: added ? "#10B981" : (current.available ? "rgba(232,213,163,0.14)" : "rgba(244,241,232,0.06)"),
                color: added ? "#fff" : (current.available ? "#EBD9A8" : "rgba(244,241,232,0.35)"),
                border: `1px solid ${added ? "#10B981" : "rgba(232,213,163,0.45)"}`,
                fontWeight: 700, fontSize: 13.5,
                cursor: current.available ? "pointer" : "default",
                transition: "background 200ms, color 200ms",
              }}
            >{added ? "✓ Ajouté" : "＋ Ajouter au panier"}</button>
            <button
              data-testid="product-detail-order"
              onClick={() => { onOrder?.(current); onClose?.(); }}
              disabled={!current.available}
              style={{
                flex: "1 1 140px",
                padding: "12px 14px", borderRadius: 12,
                background: current.available ? "linear-gradient(135deg, #F5E9C6, #E4C87F 55%, #CDA452)" : "rgba(244,241,232,0.10)",
                color: current.available ? "#3A2E10" : "rgba(244,241,232,0.4)",
                border: "none",
                fontWeight: 800, fontSize: 13.5,
                cursor: current.available ? "pointer" : "default",
              }}
            >Commander maintenant</button>
            {onViewShop && (
              <button
                data-testid="product-detail-shop"
                onClick={() => { onViewShop?.(current); onClose?.(); }}
                style={{
                  padding: "12px 14px", borderRadius: 12,
                  background: "transparent",
                  color: "#EBD9A8",
                  border: "1px solid rgba(232,213,163,0.35)",
                  fontWeight: 700, fontSize: 12.5, cursor: "pointer",
                }}
              >🏪 Boutique</button>
            )}
          </div>
        </div>

        <style>{`
          @keyframes pdIn { from { opacity: 0; transform: translateY(20px) scale(0.96) } to { opacity: 1; transform: translateY(0) scale(1) } }
        `}</style>
      </article>
    </div>
  );

  return createPortal(modal, modalRoot);
}

function _navArrowStyle(side) {
  return {
    position: "absolute",
    top: "38%", [side]: 10,
    zIndex: 3,
    width: 36, height: 36, borderRadius: "50%",
    background: "rgba(6,42,31,0.85)", color: "#F4F1E8",
    border: "1px solid rgba(232,213,163,0.35)", cursor: "pointer",
    fontSize: 24, fontWeight: 800, lineHeight: 1,
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
  };
}

function Chip({ children }) {
  return (
    <span style={{
      padding: "4px 10px", borderRadius: 999,
      background: "rgba(232,213,163,0.10)",
      border: "1px solid rgba(232,213,163,0.25)",
      color: "#EBD9A8", fontWeight: 600,
    }}>{children}</span>
  );
}


// v3.0.0-rc.8z — Description avec toggle "Voir plus" si > 220 chars.
function ProductDescription({ text }) {
  const [expanded, setExpanded] = useState(false);
  const clean = String(text || "").trim();
  if (!clean) return null;
  const isLong = clean.length > 220;
  const shown = expanded || !isLong ? clean : clean.slice(0, 210).trimEnd() + "…";
  return (
    <div style={{ marginTop: 4, borderTop: "1px solid rgba(232,213,163,0.14)", paddingTop: 10 }}>
      <div style={{ fontSize: 11, opacity: 0.6, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Description</div>
      <div
        data-testid="product-detail-description"
        style={{ fontSize: 13.5, lineHeight: 1.55, opacity: 0.9, whiteSpace: "pre-wrap" }}
      >{shown}</div>
      {isLong && (
        <button
          data-testid="product-detail-description-toggle"
          onClick={() => setExpanded((v) => !v)}
          style={{
            marginTop: 6, background: "transparent", border: "none",
            color: "#EBD9A8", fontSize: 12, fontWeight: 700, cursor: "pointer",
            padding: 0, textDecoration: "underline",
          }}
        >{expanded ? "Voir moins" : "Voir plus"}</button>
      )}
    </div>
  );
}
